import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, ArrowLeft, ExternalLink, Sparkles, X, Check, Loader2, Crown, CircleCheck, CircleAlert, RefreshCw } from "lucide-react";
import Header from "@/components/Header";
import BakerLocationSection from "@/components/BakerLocationSection";
import BroadcastComposer from "@/components/BroadcastComposer";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BENEFITS = [
  "Your pin visible to all buyers on the Cake Pop Finder map",
  "Marketplace storefront with product listings",
  "Baker's Tools — Crumb Analyzer, Recipe Scaler & more",
  "Business Tools — Costing, Invoices, Inventory, Labels",
];

export default function FinderSettings() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  const loadDiagnostic = async () => {
    setDiagLoading(true);
    try {
      const { data } = await api.get("/me/finder-diagnostic");
      setDiag(data);
    } catch { setDiag(null); }
    setDiagLoading(false);
  };

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) {
      toast.error("Please log in to manage your finder presence");
      nav("/login?next=/find-a-baker/settings");
      return;
    }
    loadDiagnostic();
    // Show the 14-day trial modal for users who don't have premium access yet
    // (subscription_status === 'none' or 'expired'). Skip for trial/active subscribers and admins.
    const sub = user.subscription_status;
    if ((sub === "none" || sub === "expired") && !user.is_premium) {
      setShowTrialModal(true);
    }
  }, [user, loading, nav]);

  const startTrial = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/start-trial");
      toast.success("14-day free trial started 🎂 — your pin will now show on the map!");
      if (refresh) await refresh();
      setShowTrialModal(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  if (loading || !user || user === false) {
    return (
      <>
        <Header />
        <div className="p-10 text-[#857163]">Loading…</div>
      </>
    );
  }

  const mode = user.is_seller ? "seller" : "independent";
  const sub = user.subscription_status;

  return (
    <>
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 lg:py-10" data-testid="finder-settings-page">
        <button
          onClick={() => nav(-1)}
          className="text-sm text-[#857163] hover:text-[#3B2C24] mb-4 inline-flex items-center gap-1"
          data-testid="finder-settings-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-6 flex items-center gap-3">
          <MapPin className="w-6 h-6 text-[#FF7E67]" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#857163]">Your finder presence</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24]">My Cake Pop Finder Info</h1>
            <p className="text-sm text-[#857163] mt-1 max-w-2xl">
              Set your business name, address, hours, dietary options, and finder logo. This is what buyers see when your pin is clicked on the map.
            </p>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <Link
            to="/find-a-baker"
            className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"
            data-testid="finder-settings-view-map"
          >
            View the map <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          {user.is_seller && (
            <Link
              to="/seller/dashboard"
              className="text-sm text-[#857163] hover:text-[#FF7E67] inline-flex items-center gap-1"
              data-testid="finder-settings-seller-dashboard"
            >
              Seller Dashboard <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        {/* Pin visibility check */}
        {diag && (
          <div
            className={`mb-4 cp-card p-4 border ${diag.visible ? "border-[#8EAC50]/30 bg-[#F1F7E5]" : "border-[#F4A83B]/30 bg-[#FFF8ED]"}`}
            data-testid="finder-visibility-widget"
          >
            <div className="flex items-start gap-3">
              {diag.visible ? (
                <CircleCheck className="w-5 h-5 shrink-0 mt-0.5 text-[#5F7A2E]" />
              ) : (
                <CircleAlert className="w-5 h-5 shrink-0 mt-0.5 text-[#B26A00]" />
              )}
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-[#857163] font-semibold mb-0.5">Pin visibility check</div>
                <div className="text-sm text-[#3B2C24] font-semibold" data-testid="finder-visibility-status">
                  {diag.reason}
                </div>
                {(diag.hints || []).length > 0 && (
                  <ul className="mt-1.5 text-xs text-[#5A4A40] space-y-0.5">
                    {diag.hints.map((h, i) => (
                      <li key={i} className="flex items-start gap-1.5">
                        <span className="text-[#B79885]">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {diag.next_step === "start_trial" && (
                  <button
                    onClick={() => setShowTrialModal(true)}
                    className="mt-2 text-xs font-semibold text-[#FF7E67] hover:underline"
                    data-testid="finder-visibility-start-trial"
                  >
                    Start 14-day free trial →
                  </button>
                )}
              </div>
              <button
                onClick={loadDiagnostic}
                disabled={diagLoading}
                className="text-xs text-[#857163] hover:text-[#3B2C24] inline-flex items-center gap-1 shrink-0"
                title="Refresh visibility check"
                data-testid="finder-visibility-refresh"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${diagLoading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>
        )}

        <div className="cp-card p-5 sm:p-6">
          <BakerLocationSection mode={mode} username={user.username} />
        </div>

        <div className="mt-6">
          <BroadcastComposer />
        </div>
        {sub === "trial" && (
          <div className="mt-6 cp-card p-4 flex items-start gap-3 border border-[#8EAC50]/20 bg-[#F1F7E5]" data-testid="finder-settings-trial-banner">
            <Sparkles className="w-5 h-5 shrink-0 text-[#5F7A2E] mt-0.5" />
            <div className="text-sm text-[#3B2C24]">
              <div className="font-semibold">You&apos;re on your free trial — {user.subscription_days_left ?? 0} days left.</div>
              <div className="text-xs text-[#857163] mt-0.5">
                Your pin is live on the map. Subscribe any time to keep it after your trial.{" "}
                <Link to="/upgrade" className="underline text-[#FF7E67] hover:opacity-80">Subscribe →</Link>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* 14-day trial pop-up — auto-opens for users without premium access */}
      <Dialog open={showTrialModal} onOpenChange={setShowTrialModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="finder-trial-modal">
          <div className="relative p-6 pb-5" style={{ background: "linear-gradient(180deg, #FFF5EA 0%, #FFFBF5 100%)" }}>
            <button
              onClick={() => setShowTrialModal(false)}
              className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full hover:bg-black/5 text-[#857163]"
              aria-label="Close"
              data-testid="finder-trial-close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[11px] uppercase tracking-widest text-[#857163] font-semibold flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /> Premium feature
            </div>
            <DialogHeader className="mt-2">
              <DialogTitle className="font-heading text-3xl text-[#3B2C24] leading-tight">
                Get your pin on the map <span aria-hidden="true">🎂</span>
              </DialogTitle>
              <DialogDescription className="text-sm text-[#5A4A40] mt-2">
                The Cake Pop Finder is a Premium feature. Start your <b>14-day free trial</b> right now — no card required — and buyers will see your pin the moment you save your info.
              </DialogDescription>
            </DialogHeader>
            <ul className="mt-4 space-y-2 text-sm text-[#3B2C24]">
              {BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#8EAC50" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 flex flex-col gap-2">
              {sub === "none" ? (
                <button
                  onClick={startTrial}
                  disabled={busy}
                  className="w-full py-3 rounded-full text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-sm"
                  style={{ background: "linear-gradient(135deg, #FF7E67, #FF9E85)" }}
                  data-testid="finder-trial-start-btn"
                >
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <><Sparkles className="w-4 h-4" /> Start 14-day free trial</>}
                </button>
              ) : (
                <Link
                  to="/upgrade"
                  onClick={() => setShowTrialModal(false)}
                  className="w-full py-3 rounded-full text-white font-semibold text-sm hover:opacity-90 inline-flex items-center justify-center gap-2 shadow-sm"
                  style={{ background: "linear-gradient(135deg, #FF7E67, #FF9E85)" }}
                  data-testid="finder-trial-subscribe-btn"
                >
                  Subscribe — $7/mo
                </Link>
              )}
              <button
                onClick={() => setShowTrialModal(false)}
                className="w-full py-2 text-xs text-[#857163] hover:text-[#3B2C24] transition"
                data-testid="finder-trial-skip"
              >
                Not now — I&apos;ll just save my info for later
              </button>
            </div>
            <div className="mt-3 text-[10px] text-[#857163] text-center">
              You can save your finder info anytime — it&apos;ll go live automatically the moment your trial or subscription starts.
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
